import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import LiveCallsCard from '../components/concurrency/LiveCallsCard';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  DollarSign,
  Clock,
  Users,
  ChevronRight,
  Plus,
  AlertCircle,
  CheckCircle,
  XCircle,
  Repeat,
  Calendar
} from 'lucide-react';

const ROI_PERIOD_LABELS = { day: 'Today', week: 'Last 7 days', mtd: 'Month to date', '30d': 'Last 30 days', custom: 'Custom range' };

const WorkspaceDashboard = () => {
  const { workspaceId } = useParams();
  const { api, user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roi, setRoi] = useState({ total_upfront: 0, mrr: 0, active_clients: 0, priced_sub_accounts: 0 });
  const [roiPeriod, setRoiPeriod] = useState('mtd');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [appts, setAppts] = useState({ items: [], total: 0 });
  // Workspace Usage Summary widget — sticky period preference per user
  const STORAGE_KEY = 'voxinity:dashboard-usage-period';
  const [usagePeriod, setUsagePeriod] = useState(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      return ['7d', '30d', '90d'].includes(saved) ? saved : '30d';
    } catch { return '30d'; }
  });
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      fetchWorkspace();
      fetchAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    if (roiPeriod === 'custom' && (!customRange.start || !customRange.end)) return;
    fetchRoi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, roiPeriod, customRange]);

  const fetchWorkspace = async () => {
    try {
      const response = await api.get(`/api/workspaces/${workspaceId}`);
      setWorkspace(response.data);
    } catch (error) {
      toast.error('Failed to load workspace');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getRoiWindow = () => {
    const now = new Date();
    if (roiPeriod === 'day') return { start: new Date(now.getFullYear(), now.getMonth(), now.getDate()), end: now };
    if (roiPeriod === 'week') return { start: new Date(now.getTime() - 7 * 86400000), end: now };
    if (roiPeriod === 'mtd') return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
    if (roiPeriod === '30d') return { start: new Date(now.getTime() - 30 * 86400000), end: now };
    if (roiPeriod === 'custom' && customRange.start && customRange.end) {
      const end = new Date(customRange.end);
      end.setHours(23, 59, 59, 999);
      return { start: new Date(customRange.start), end };
    }
    return null;
  };

  const fetchRoi = async () => {
    try {
      const win = getRoiWindow();
      const qs = win ? `?start=${encodeURIComponent(win.start.toISOString())}&end=${encodeURIComponent(win.end.toISOString())}` : '';
      const r = await api.get(`/api/workspaces/${workspaceId}/roi-snapshot${qs}`);
      setRoi(r.data || {});
    } catch (e) { /* optional */ }
  };

  const fetchAppointments = async () => {
    try {
      const r = await api.get(`/api/workspaces/${workspaceId}/appointments-by-sub-account`);
      setAppts(r.data || { items: [], total: 0 });
    } catch (e) { /* optional */ }
  };

  // Auto-refetch whenever the period toggle changes. localStorage persists
  // the selection across reloads (per spec — sticky last-selected, default 30d).
  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    (async () => {
      setUsageLoading(true);
      try {
        const r = await api.get(`/api/workspaces/${workspaceId}/usage?period=${usagePeriod}`);
        if (!cancelled) setUsage(r.data || null);
      } catch (e) { if (!cancelled) setUsage(null); }
      finally { if (!cancelled) setUsageLoading(false); }
    })();
    try { window.localStorage.setItem(STORAGE_KEY, usagePeriod); } catch { /* noop */ }
    return () => { cancelled = true; };
  }, [workspaceId, usagePeriod]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!workspace) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="mx-auto text-gray-600 mb-4" size={48} />
            <p className="text-gray-400">Workspace not found</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const { stats, sub_accounts } = workspace;
  // Calculate sub-account health
  const activeAccounts = sub_accounts?.filter(sa => sa.status === 'active').length || 0;
  const lowActivityAccounts = sub_accounts?.filter(sa => sa.status === 'low_activity').length || 0;
  const inactiveAccounts = sub_accounts?.filter(sa => sa.status === 'inactive').length || 0;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8" data-testid="workspace-dashboard">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-semibold text-white mb-1" data-testid="dashboard-greeting">
                Hey {(user?.name || 'there').split(' ')[0]} 👋
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-gray-400" data-testid="dashboard-subtitle">{workspace.name} performance overview</p>
                {workspace.is_beta && !workspace.is_lifetime && (
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-extrabold border tracking-[0.2em]"
                    style={{ background: 'linear-gradient(135deg,#cbd5e1,#94a3b8)', color: '#0a0b0f', borderColor: 'rgba(148,163,184,0.5)' }}
                    data-testid="workspace-header-beta-badge"
                  >
                    BETA
                  </span>
                )}
                {workspace.is_lifetime && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-amber-500/25 to-yellow-400/25 text-amber-300 border border-amber-400/30 tracking-[0.2em]"
                        data-testid="workspace-header-lifetime-badge">
                    LIFETIME
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-gray-500">Period Filter</span>
              <Select value={roiPeriod} onValueChange={setRoiPeriod}>
                <SelectTrigger className="w-[160px] h-9 bg-white/[0.03] border-white/10 text-white text-sm" data-testid="roi-period-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day" data-testid="roi-period-day">Day</SelectItem>
                  <SelectItem value="week" data-testid="roi-period-week">Week</SelectItem>
                  <SelectItem value="mtd" data-testid="roi-period-mtd">Month to date</SelectItem>
                  <SelectItem value="30d" data-testid="roi-period-30d">30 days</SelectItem>
                  <SelectItem value="custom" data-testid="roi-period-custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
              {roiPeriod === 'custom' && (
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={customRange.start}
                    onChange={(e) => setCustomRange((c) => ({ ...c, start: e.target.value }))}
                    className="h-9 px-2 rounded-md bg-white/[0.03] border border-white/10 text-sm text-white [color-scheme:dark]"
                    data-testid="roi-custom-start"
                  />
                  <span className="text-xs text-gray-500">to</span>
                  <input
                    type="date"
                    value={customRange.end}
                    onChange={(e) => setCustomRange((c) => ({ ...c, end: e.target.value }))}
                    className="h-9 px-2 rounded-md bg-white/[0.03] border border-white/10 text-sm text-white [color-scheme:dark]"
                    data-testid="roi-custom-end"
                  />
                </div>
              )}
            </div>
          </div>
        </motion.div>

        <LiveCallsCard workspaceId={workspaceId} />

        {/* ROI Snapshot — Total Upfront · MRR · Active Clients */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="roi-snapshot">
          {/* Total Upfront */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-xl p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-400/5 to-transparent pointer-events-none" />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <DollarSign className="text-emerald-400" size={24} />
                <span className="text-xs text-gray-500" data-testid="roi-upfront-period-label">{ROI_PERIOD_LABELS[roiPeriod]}</span>
              </div>
              <p className="text-3xl font-semibold text-emerald-400 mb-1 tabular-nums" data-testid="roi-total-upfront">
                ${Number(roi.total_upfront || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
              <p className="text-sm text-gray-400">Total Upfront</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{roi.priced_sub_accounts || 0} sub-account{roi.priced_sub_accounts === 1 ? '' : 's'} priced</p>
            </div>
          </motion.div>

          {/* Monthly Recurring Revenue */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="glass-card rounded-xl p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-400/5 to-transparent pointer-events-none" />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <Repeat className="text-teal-400" size={24} />
                <span className="text-xs text-gray-500" data-testid="roi-mrr-period-label">{ROI_PERIOD_LABELS[roiPeriod]}</span>
              </div>
              <p className="text-3xl font-semibold text-teal-400 mb-1 tabular-nums" data-testid="roi-mrr">
                ${Number(roi.mrr || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
              <p className="text-sm text-gray-400">Monthly Recurring Revenue</p>
              <p className="text-[10px] text-gray-500 mt-0.5">New MRR added in period</p>
            </div>
          </motion.div>

          {/* Active Clients */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card rounded-xl p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-violet-400/5 to-transparent pointer-events-none" />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <Users className="text-violet-400" size={24} />
                <span className="text-xs text-gray-500">{stats.total_clients} total</span>
              </div>
              <p className="text-3xl font-semibold text-white mb-1 tabular-nums" data-testid="roi-active-clients">
                {roi.active_clients ?? stats.active_clients}
              </p>
              <p className="text-sm text-gray-400">Active Clients</p>
              <p className="text-[10px] text-gray-500 mt-0.5">New in period</p>
            </div>
          </motion.div>
        </div>

        {/* Appointments by Sub-Account */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="glass-card rounded-xl p-6 mb-8" data-testid="appointments-by-sub-account">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2.5">
              <Calendar className="text-amber-400" size={20} />
              <h3 className="text-base font-semibold text-white">Appointments by Sub-Account</h3>
              <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">This month</span>
            </div>
            <div className="text-xs text-gray-400 tabular-nums"><span className="text-white font-semibold">{appts.total}</span> total</div>
          </div>
          {appts.items.length === 0 ? (
            <div className="text-center py-8 text-sm text-gray-500">No sub-accounts yet</div>
          ) : (
            <div className="space-y-2">
              {(() => {
                const maxVal = Math.max(1, ...appts.items.map(i => i.appointments));
                return appts.items.map((it) => {
                  const pct = Math.round((it.appointments / maxVal) * 100);
                  const bucket = it.appointments === 0 ? 'muted' : it.appointments >= 10 ? 'hot' : it.appointments >= 3 ? 'warm' : 'cool';
                  const dot = {muted: 'bg-gray-600', cool: 'bg-gray-400', warm: 'bg-amber-400', hot: 'bg-emerald-400'}[bucket];
                  const bar = {muted: 'bg-white/5', cool: 'bg-white/10', warm: 'bg-amber-400/30', hot: 'bg-emerald-400/30'}[bucket];
                  return (
                    <Link
                      key={it.sub_account_id}
                      to={`/workspace/${workspaceId}/sub-account/${it.sub_account_id}`}
                      className="group flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.04] transition-colors"
                      data-testid={`appt-row-${it.sub_account_id}`}
                    >
                      <span className={`w-2 h-2 rounded-full ${dot} flex-shrink-0`} />
                      <span className="text-sm text-white font-medium truncate w-48">{it.name}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-white/[0.03] overflow-hidden">
                        <div className={`h-full ${bar} transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-sm font-bold text-white tabular-nums w-10 text-right">{it.appointments}</span>
                      <ChevronRight size={14} className="text-gray-600 group-hover:text-gray-400 transition-colors" />
                    </Link>
                  );
                });
              })()}
            </div>
          )}
        </motion.div>

        {/* Workspace Usage Summary — total minutes + total spend across all
            sub-accounts. Period toggle (7d/30d/90d) persisted to
            localStorage. For admin workspaces (Voxinity AI / Skye AI), the
            "Total Spent" stat shows REAL provider cost (wholesale rates from
            ElevenLabs / OpenAI) instead of the in-app revenue figure. */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="mb-6 glass-card rounded-xl p-6"
          data-testid="workspace-usage-summary-card"
        >
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-lg font-semibold text-white">Workspace Usage</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {usage?.is_admin_workspace
                  ? 'Aggregate usage and provider cost across all sub-accounts'
                  : 'Aggregate usage and spend across all sub-accounts'}
              </p>
            </div>
            <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg p-1">
              {['7d', '30d', '90d'].map(p => (
                <button
                  key={p}
                  onClick={() => setUsagePeriod(p)}
                  data-testid={`usage-period-${p}`}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    usagePeriod === p
                      ? 'bg-teal-400/20 text-teal-300'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {p === '7d' ? '7 days' : p === '30d' ? '30 days' : '90 days'}
                </button>
              ))}
            </div>
          </div>

          {usageLoading && !usage ? (
            <div className="h-24 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Total Minutes */}
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="total-minutes-stat">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={16} className="text-teal-400" />
                    <span className="text-xs text-gray-400 uppercase tracking-wider">Total Minutes Used</span>
                  </div>
                  <p className="text-3xl font-semibold text-white tabular-nums">
                    {(usage?.summary?.total_minutes ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                    <span className="text-base font-normal text-gray-500 ml-1.5">min</span>
                  </p>
                </div>

                {/* Total Spend (revenue OR provider cost depending on workspace) */}
                <div
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-5"
                  data-testid={usage?.is_admin_workspace ? 'total-cost-stat' : 'total-spent-stat'}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign size={16} className={usage?.is_admin_workspace ? 'text-amber-400' : 'text-violet-400'} />
                    <span className="text-xs text-gray-400 uppercase tracking-wider">
                      {usage?.is_admin_workspace ? 'Provider Cost' : 'Total Spent'}
                    </span>
                    {usage?.is_admin_workspace && (
                      <span
                        className="ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300"
                        title="Admin workspace — shows wholesale rates (ElevenLabs, OpenAI, KB) instead of in-app revenue"
                      >
                        ADMIN
                      </span>
                    )}
                  </div>
                  <p className="text-3xl font-semibold text-white tabular-nums">
                    ${(
                      usage?.is_admin_workspace
                        ? (usage?.summary?.total_provider_cost ?? 0)
                        : (usage?.summary?.total_cost ?? 0)
                    ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              {/* Per-type breakdown — only render types with non-zero usage */}
              {usage?.by_type && Object.entries(usage.by_type).some(([, v]) => (v?.quantity || 0) > 0) && (
                <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-gray-400">
                  {Object.entries(usage.by_type)
                    .filter(([, v]) => (v?.quantity || 0) > 0)
                    .map(([type, v]) => {
                      const isVoice = type === 'voice' || type === 'voice_inbound' || type === 'voice_outbound';
                      const isMsg = type === 'chat' || type === 'sms_inbound' || type === 'sms_outbound';
                      const label = isVoice
                        ? `Voice (${type === 'voice_inbound' ? 'in' : type === 'voice_outbound' ? 'out' : 'all'})`
                        : isMsg ? 'Messages' : type === 'kb' ? 'KB queries' : type;
                      const unit = isVoice ? 'min' : '';
                      const qty = (v.quantity || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
                      return (
                        <span key={type} className="inline-flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-teal-400/60" />
                          <span className="text-gray-300">{label}:</span>
                          <span className="text-white tabular-nums">{qty}{unit ? ` ${unit}` : ''}</span>
                        </span>
                      );
                    })}
                </div>
              )}
            </>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Usage by Sub-Account */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="glass-card rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Usage by Sub-Account</h3>
              <Link
                to={`/workspace/${workspaceId}/sub-accounts`}
                className="text-sm text-teal-400 hover:text-teal-300 flex items-center gap-1"
              >
                View All <ChevronRight size={14} />
              </Link>
            </div>

            {sub_accounts && sub_accounts.length > 0 ? (
              <div className="space-y-4">
                {sub_accounts.slice(0, 5).map((account, index) => {
                  const percentage = workspace.minutes_included > 0 
                    ? (account.minutes / workspace.minutes_included) * 100 
                    : 0;
                  
                  return (
                    <div key={account.sub_account_id || index}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-300">{account.name}</span>
                        <span className="text-sm text-gray-400">{account.minutes?.toFixed(0) || 0} min</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(percentage, 100)}%` }}
                          transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                          className="h-full bg-gradient-to-r from-teal-400 to-phosphor-400 rounded-full"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="mx-auto text-gray-600 mb-3" size={32} />
                <p className="text-gray-400 text-sm mb-3">No sub-accounts yet</p>
                <Link
                  to={`/workspace/${workspaceId}/sub-accounts`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-400 text-space-900 text-sm font-medium hover:bg-teal-500 transition-colors"
                >
                  <Plus size={16} /> Add Sub-Account
                </Link>
              </div>
            )}
          </motion.div>

          {/* Sub-Account Health */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card rounded-xl p-6"
          >
            <h3 className="text-lg font-semibold text-white mb-6">Sub-Account Health</h3>

            <div className="grid grid-cols-3 gap-4">
              {/* Active */}
              <div className="p-4 rounded-xl bg-green-400/10 border border-green-400/20">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="text-green-400" size={18} />
                  <span className="text-xs text-green-400 font-medium uppercase">Active</span>
                </div>
                <p className="text-2xl font-semibold text-white">{activeAccounts}</p>
              </div>

              {/* Low Activity */}
              <div className="p-4 rounded-xl bg-yellow-400/10 border border-yellow-400/20">
                <div className="flex items-center gap-2 mb-3">
                  <AlertCircle className="text-yellow-400" size={18} />
                  <span className="text-xs text-yellow-400 font-medium uppercase">Low Activity</span>
                </div>
                <p className="text-2xl font-semibold text-white">{lowActivityAccounts}</p>
              </div>

              {/* Inactive */}
              <div className="p-4 rounded-xl bg-gray-400/10 border border-gray-400/20">
                <div className="flex items-center gap-2 mb-3">
                  <XCircle className="text-gray-400" size={18} />
                  <span className="text-xs text-gray-400 font-medium uppercase">Inactive</span>
                </div>
                <p className="text-2xl font-semibold text-white">{inactiveAccounts}</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <Link
                to={`/workspace/${workspaceId}/sub-accounts`}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-400 text-space-900 font-medium hover:bg-teal-500 transition-colors"
              >
                <Plus size={18} />
                New Sub-Account
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Plan Usage — only relevant on metered plans. Lifetime / Pro
            users get a celebratory "Unlimited" pill instead of a confusing
            0/10 bar (10 was the legacy free-tier minute cap). When there's
            no plan limit configured at all (minutes_included = 0 AND not
            lifetime), hide the widget entirely — better than showing a
            divide-by-zero NaN%. */}
        {(workspace.is_lifetime || workspace.minutes_included > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mt-6 glass-card rounded-xl p-6"
          data-testid="plan-usage-widget"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Plan Usage</h3>
            <Link
              to={`/workspace/${workspaceId}/billing`}
              className="text-sm text-teal-400 hover:text-teal-300 flex items-center gap-1"
            >
              Billing Details <ChevronRight size={14} />
            </Link>
          </div>

          {workspace.is_lifetime ? (
            // Unlimited / Lifetime plan: show celebratory state instead of
            // a meaningless 0/10 bar. Filled teal bar + ∞ glyph + "Unlimited".
            <div className="flex items-center gap-4" data-testid="plan-usage-unlimited">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Voice Minutes</span>
                  <span className="text-sm font-semibold text-teal-400 flex items-center gap-1.5">
                    <span className="text-base leading-none">∞</span> Unlimited
                  </span>
                </div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-teal-400 via-cyan-400 to-violet-400" style={{ width: '100%' }} />
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-semibold text-white">
                  {workspace.minutes_used?.toLocaleString() || 0}
                </p>
                <p className="text-xs text-gray-400">Min used</p>
              </div>
            </div>
          ) : (
            // Metered plan: real progress bar + percent
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Voice Minutes</span>
                  <span className="text-sm text-white">
                    {workspace.minutes_used?.toLocaleString() || 0} / {workspace.minutes_included?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      (workspace.minutes_used / workspace.minutes_included) > 0.9
                        ? 'bg-red-400'
                        : (workspace.minutes_used / workspace.minutes_included) > 0.7
                        ? 'bg-yellow-400'
                        : 'bg-teal-400'
                    }`}
                    style={{ width: `${Math.min((workspace.minutes_used / workspace.minutes_included) * 100, 100)}%` }}
                  />
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-semibold text-white">
                  {Math.round((workspace.minutes_used / workspace.minutes_included) * 100)}%
                </p>
                <p className="text-xs text-gray-400">Used</p>
              </div>
            </div>
          )}
        </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default WorkspaceDashboard;
